/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * StudentMessagesCard - Quick access card to messaging with coaches
 * Shows unread count and links to the Messages page
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageSquare, ChevronRight, Loader2 } from 'lucide-react'
import messagingService from '../services/messagingService'
import { createLogger } from '../utils/logger'

const logger = createLogger('StudentMessagesCard')

const StudentMessagesCard = () => {
  const navigate = useNavigate()
  const [unreadCount, setUnreadCount] = useState(0)
  const [availableCoaches, setAvailableCoaches] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [unreadResponse, coachesResponse] = await Promise.all([
        messagingService.getUnreadCount(),
        messagingService.getAvailableCoaches()
      ])

      if (unreadResponse.success) {
        setUnreadCount(unreadResponse.unreadCount || 0)
      }
      if (coachesResponse.success) {
        setAvailableCoaches(coachesResponse.coaches || [])
      }
    } catch (error) {
      logger.error('Error loading messaging data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Don't show if no coaches available for messaging
  if (!loading && availableCoaches.length === 0) {
    return null
  }

  return (
    <div className="card border-l-4 border-l-primary-500">
      <div className="card-body">
        <button
          onClick={() => navigate('/messages')}
          className="w-full flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>

            <div className="text-left">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                Direct Messages
              </h3>
              {loading ? (
                <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Loading...
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {unreadCount > 0
                    ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}`
                    : 'Private conversation with your coach'}
                </p>
              )}
            </div>
          </div>

          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" />
        </button>
      </div>
    </div>
  )
}

export default StudentMessagesCard
